import React from 'react'
import {
  cgmSteloDoubleImage,
  cgmSteloImage,
  glucoseChartMealImage,
  glucoseScoreWheelImage,
  marketplaceCgmProgramsImage,
  marketplaceNnVideoCallsImage,
  marketplaceNutritionistSupportImage,
  marketplaceRunnerImage,
} from '@assets/images'
import { Product } from '@src/types'
import SuccessIcon from '@src/assets/svgs/successIcon'
import RewardIcon from '@src/assets/svgs/rewardIcon'
import CalmIcon from '@src/assets/svgs/calmIcon'
import { StyleService } from '@src/style/service'
import { WhiteText } from './WhiteText'

export enum VideoCallProductTypes {
  Insurance = 'Insurance',
  Paid = 'Paid',
  HealthCoaching = 'HealthCoaching',
}

export const VIDEO_CALL_PRODUCT_DEFINITION = {
  [VideoCallProductTypes.Insurance]: {
    title: 'Nutritionist Video Calls',
    description: 'In-network, in-the-moment video consultations with expert nutritionists.',
  },
  [VideoCallProductTypes.Paid]: {
    title: 'Nutritionist Video Calls',
    description: 'In-network, in-the-moment video consultations with expert nutritionists.',
  },
  [VideoCallProductTypes.HealthCoaching]: {
    title: 'Coaching Video Calls',
    description: 'In-the-moment video consultations with expert health coaches.',
  },
}

const styles = StyleService.create({
  title: {
    fontSize: 29,
    lineHeight: 31,
    fontWeight: 400,
  },
  italic: {
    fontStyle: 'italic',
    fontWeight: 700,
  },
})

const VIDEO_CALL_TITLE = {
  [VideoCallProductTypes.Insurance]: (
    <WhiteText type="title-1" style={styles.title}>
      Expert{' '}
      <WhiteText type="title-1" style={[styles.title, styles.italic]}>
        nutritionist support
      </WhiteText>
      ,{'\n'}now covered by insurance.
    </WhiteText>
  ),
  [VideoCallProductTypes.Paid]: (
    <WhiteText type="title-1" style={styles.title}>
      Expert{' '}
      <WhiteText type="title-1" style={[styles.title, styles.italic]}>
        nutritionist support
      </WhiteText>
      ,{'\n'}pay as you go.
    </WhiteText>
  ),
  [VideoCallProductTypes.HealthCoaching]: (
    <WhiteText type="title-1" style={styles.title}>
      1-on-1 Health Coaching{'\n'}that actually sticks
    </WhiteText>
  ),
}

const VIDEO_CALL_BENEFITS = {
  [VideoCallProductTypes.Insurance]: [
    {
      title: 'Covered by most insurances',
      description: 'Most members pay $0 out-of-pocket\nfor 3 or more video calls per year.',
      getImage: (isDarkMode: boolean) => <SuccessIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 48,
      height: 48,
    },
    {
      title: 'Real experts, real care and results',
      description:
        'Ditch the generic advice for personalized flexible support from top nutritionists. ',
      getImage: (isDarkMode: boolean) => <RewardIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 40,
      height: 54,
    },
    {
      title: 'Regular check-ins',
      description: 'Your dietitian helps you expand food choices and build lasting habits.',
      getImage: (isDarkMode: boolean) => <CalmIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 46,
      height: 46,
    },
  ],
  [VideoCallProductTypes.Paid]: [
    {
      title: 'Only :video_call_price for 30 minutes',
      description: 'Affordable access to leading nutritionists when you need it.',
      getImage: (isDarkMode: boolean) => <SuccessIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 48,
      height: 48,
    },
    {
      title: 'Real experts, real care and results',
      description:
        'Ditch the generic advice for personalized flexible support from top nutritionists. ',
      getImage: (isDarkMode: boolean) => <RewardIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 40,
      height: 54,
    },
    {
      title: 'Regular check-ins',
      description: 'Your dietitian helps you expand food choices and build lasting habits.',
      getImage: (isDarkMode: boolean) => <CalmIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 46,
      height: 46,
    },
  ],
  [VideoCallProductTypes.HealthCoaching]: [
    {
      title: 'Real Experts, Real Advice',
      description:
        'Skip the generic advice. Get meal tweaks, habit support and next steps from top coaches.',
      getImage: (isDarkMode: boolean) => <SuccessIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 48,
      height: 48,
    },
    {
      title: 'Tailored to You',
      description: 'Your coach adapts support as your routine, energy, or goals evolve.',
      getImage: (isDarkMode: boolean) => <RewardIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 40,
      height: 54,
    },
    {
      title: 'No Guilt, Just Progress',
      description: 'Expand your food choices and build sustainable habits that actually last.',
      getImage: (isDarkMode: boolean) => <CalmIcon color={isDarkMode ? '#fff' : '#102F32'} />,
      width: 46,
      height: 46,
    },
  ],
}

const VIDEO_CALL_DESCRIPTION = {
  [VideoCallProductTypes.Insurance]:
    'Get the right advice that moves you toward your health goals' +
    ' from a registered dietitian specialized in your condition.',
  [VideoCallProductTypes.Paid]:
    'Get the right advice that moves you toward your health goals' +
    ' from a registered dietitian specialized in your condition.',
  [VideoCallProductTypes.HealthCoaching]:
    'Get personalized advice from expert coaches to help you reach your goals--no cookie cutter plans,' +
    ' just support that fits your lifestyle.',
}

export const OWN_SENSOR_BENEFITS = {
  [VideoCallProductTypes.Insurance]: [
    {
      title: 'Discounted biosensors',
      description: 'Discounted biosensors whenever you need them',
      getImage: (_isDarkMode: boolean) => cgmSteloDoubleImage,
      width: 83,
      height: 92,
    },
    {
      title: 'Glucose visualization',
      description: 'Understand how your meals & activities affect your glucose response',
      getImage: (_isDarkMode: boolean) => glucoseChartMealImage,
      width: 84,
      height: 73,
    },
    {
      title: 'Nutritionist video calls',
      description: '$0 out of pocket 1-on-1 video calls with our expert nutritionists',
      getImage: (_isDarkMode: boolean) => marketplaceNnVideoCallsImage,
      width: 60,
      height: 69,
    },
  ],
  [VideoCallProductTypes.Paid]: [
    {
      title: 'Discounted biosensors',
      description: 'Discounted biosensors whenever you need them',
      getImage: (_isDarkMode: boolean) => cgmSteloDoubleImage,
      width: 83,
      height: 92,
    },
    {
      title: 'Glucose visualization',
      description: 'Understand how your meals & activities affect your glucose response',
      getImage: (_isDarkMode: boolean) => glucoseChartMealImage,
      width: 84,
      height: 73,
    },
    {
      title: 'Nutritionist video calls',
      description: '$0 out of pocket 1-on-1 video calls with our expert nutritionists',
      getImage: (_isDarkMode: boolean) => marketplaceNnVideoCallsImage,
      width: 60,
      height: 69,
    },
  ],
  [VideoCallProductTypes.HealthCoaching]: [
    {
      title: 'Glucose visualization',
      description: 'Understand how your meals & activities affect your glucose response.',
      getImage: (isDarkMode: boolean) => glucoseScoreWheelImage(isDarkMode),
      width: 83,
      height: 59,
    },
    {
      title: 'Meal scores',
      description: 'Detailed insights on how a meal\naffected your blood glucose levels.',
      getImage: (_isDarkMode: boolean) => glucoseChartMealImage,
      width: 84,
      height: 73,
    },
    {
      title: 'Data-driven Coaching',
      description: 'Your coach uses your glucose data to tailor your plan.',
      getImage: (_isDarkMode: boolean) => marketplaceNnVideoCallsImage,
      width: 60,
      height: 69,
    },
  ],
}

export enum ProductExplanationScreenTypes {
  CgmProducts = 'CgmProducts',
  InsuranceVideoCalls = 'InsuranceVideoCalls',
  PaidVideoCalls = 'PaidVideoCalls',
  OwnSensor = 'OwnSensor',
}

export const getProductExplanationScreenDefinition = (
  videoCallProductType: VideoCallProductTypes,
  paidVideoCallProduct: Product | null,
) => {
  return {
    [ProductExplanationScreenTypes.CgmProducts]: {
      title: (
        <WhiteText type="title-1" style={styles.title}>
          Your body’s data.{'\n'}Our expert{' '}
          <WhiteText type="title-1" style={[styles.title, styles.italic]}>
            insights
          </WhiteText>
          .
        </WhiteText>
      ),
      description:
        "Personalized solutions rooted in science, based on your body's data. Balance your " +
        'glucose, boost your metabolism, and shed the weight, for good.',
      backgroundImage: marketplaceCgmProgramsImage,
      backgroundImageColor: '#102F32',
      backgroundImageOpacity: 0.7,
      benefits: [
        {
          title: 'Bluetooth sensors',
          description: 'Monthly shipments of next generation real-time automatic sensors.',
          getImage: (_isDarkMode: boolean) => cgmSteloImage,
          width: 67,
          height: 78,
        },
        {
          title: 'Glucose visualization',
          description: 'Understand how your meals & activities affect your glucose response.',
          getImage: (isDarkMode: boolean) => glucoseScoreWheelImage(isDarkMode),
          width: 83,
          height: 59,
        },
        {
          title: 'Meal scores',
          description: 'Detailed insights on how a meal\naffected your blood glucose levels.',
          getImage: (_isDarkMode: boolean) => glucoseChartMealImage,
          width: 84,
          height: 73,
        },
      ],
    },
    [ProductExplanationScreenTypes.InsuranceVideoCalls]: {
      title: VIDEO_CALL_TITLE[videoCallProductType],
      description: VIDEO_CALL_DESCRIPTION[videoCallProductType],
      backgroundImage: marketplaceNutritionistSupportImage,
      backgroundImageColor: '#122D36',
      backgroundImageOpacity: 0.5,
      benefits: VIDEO_CALL_BENEFITS[videoCallProductType],
    },
    [ProductExplanationScreenTypes.PaidVideoCalls]: {
      title: VIDEO_CALL_TITLE[videoCallProductType],
      description: VIDEO_CALL_DESCRIPTION[videoCallProductType],
      backgroundImage: marketplaceNutritionistSupportImage,
      backgroundImageColor: '#122D36',
      backgroundImageOpacity: 0.5,
      benefits: VIDEO_CALL_BENEFITS[videoCallProductType].map((benefit) => {
        if (benefit.title.includes(':video_call_price') && paidVideoCallProduct) {
          return {
            ...benefit,
            title: benefit.title.replace(':video_call_price', `$${paidVideoCallProduct.price}`),
          }
        }

        return benefit
      }),
    },
    [ProductExplanationScreenTypes.OwnSensor]: {
      title: (
        <WhiteText type="title-1" style={styles.title}>
          Your body’s data.{'\n'}Our expert{' '}
          <WhiteText type="title-1" style={[styles.title, styles.italic]}>
            insights
          </WhiteText>
          .
        </WhiteText>
      ),
      description:
        "Personalized solutions rooted in science, based on your body's data. Balance your " +
        'glucose, boost your metabolism, and shed the weight, for good.',
      backgroundImage: marketplaceRunnerImage,
      backgroundImageColor: '#082829B2',
      backgroundImageOpacity: 0.7,
      benefits: OWN_SENSOR_BENEFITS[videoCallProductType],
    },
  }
}
